import { useEffect, useRef, useState } from 'react';
import { resolveBrand, type ResolvedBrand, type Tenant } from '@kawai/shared';
import { fetchTenantById } from './repositories/tenants.js';

// 純粋な解決ロジック（resolveBrand / ResolvedBrand）は `@kawai/shared` に集約済（S-10）。
// 本ファイルには React / Firebase 依存の hook と、このアプリ固有のデフォルトだけを残す。
export type { ResolvedBrand };

/**
 * A-34: tenant-promo のデフォルトブランド。
 * tenant doc 取得前 / 取得失敗 / saas-admin（tenantId=null）時に表示する。
 * 表示名は「セルフ武生21SS」に統一（tenant doc も同値を返す）。
 */
export const APP_DEFAULT_BRAND: ResolvedBrand = {
  brandName: 'セルフ武生21SS',
  primaryColor: '#0F172A',
};

export type TenantBrandStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseTenantBrandResult {
  status: TenantBrandStatus;
  brand: ResolvedBrand;
}

/**
 * S-10: claims.tenantId に紐づく `tenants/{tid}` doc を取得し、
 * `customizations` を解決してブランド表示値を返す hook（S-04 tenant-portal と同一実装）。
 *
 * - `tenantId === null`（saas-admin）→ APP_DEFAULT_BRAND を即返す（fetch しない）
 * - 取得失敗時もアプリは止めず APP_DEFAULT_BRAND にフォールバック（status='error'）
 * - test 時は `fetcher` を差し替え可能
 *
 * fetch は `tenantId` が変わったときだけ走る。Layout は AuthGuard 配下で 1 度だけ
 * マウントされるため、ページ遷移ごとには再 fetch しない（= アプリ起動時 1 回 / S-10 要件）。
 */
export function useTenantBrand(
  tenantId: string | null,
  fetcher: (id: string) => Promise<Tenant | null> = fetchTenantById,
): UseTenantBrandResult {
  // fetcher を初回値に固定する。利用側が無名 arrow を渡しても useEffect の deps に
  // fetcher を含めないことで、毎レンダー再 fetch（無限ループ）になるのを防ぐ
  // （S-04 PR #88 指摘 / コピー時の事故防止）。
  const fetcherRef = useRef(fetcher);

  const [state, setState] = useState<UseTenantBrandResult>({
    status: tenantId ? 'loading' : 'idle',
    brand: APP_DEFAULT_BRAND,
  });

  useEffect(() => {
    if (!tenantId) {
      setState({ status: 'idle', brand: APP_DEFAULT_BRAND });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ status: 'loading', brand: prev.brand }));
    fetcherRef
      .current(tenantId)
      .then((tenant) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          brand: resolveBrand(tenant, APP_DEFAULT_BRAND),
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 致命ではないので APP_DEFAULT_BRAND にフォールバック
        // eslint-disable-next-line no-console
        console.error('[tenantBrand] fetch failed:', err);
        setState({ status: 'error', brand: APP_DEFAULT_BRAND });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return state;
}
